import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import ProgressBar from '@avalara/bat-apps-shared/client/components/ProgressBar';
import { SCol, SContainer, SRow } from '@avalara/skylab-sdk/react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Pages } from '../../../../../types/offers';
import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import { completePlanSelect } from '../../../lib/side-effects/offer-flow';
import { stepNames } from '../../../lib/side-effects/progress-bar';
import { selectState } from '../../../selectors';
import { getSelectedPlanName } from '../../../selectors/order';
import { setCurrentOfferPage } from '../../../store/offer';
import { selectPlan } from '../../../store/order';
import AvataxSandboxPlanSelect from './avatax-sandbox';
import ExemptionsPlanSelect from './exemptions';
import IossPlanSelect from './ioss';

import type {
  ExemptionsPlanClientDetails,
  IossPlanClientDetails,
  OfferPlanClientDetails,
} from '../../../../../types/offers';

const OfferPlanSelect = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {
    offer: { offerFlow, plans, pages },
  } = useSelector(selectState);

  const selectedPlanName = useSelector(getSelectedPlanName);

  // goToNextOfferStep (dispatched below via completePlanSelect) resolves the page to advance to
  // partly from `history.location.pathname`, which it also writes to. Calling it twice in a row
  // for this same single-plan auto-advance would read back its own just-written URL and advance
  // an extra step, so this ref makes sure the auto-advance only ever fires once per mount, however
  // many times this effect happens to run (e.g. under React.StrictMode's dev-only double-invoke).
  const hasAutoAdvancedRef = useRef(false);

  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.Plans));
    dispatch(setProgressBarCurrentStep(stepNames.selectPlan));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (plans.length === 0) return;
    if (plans.length === 1 && !pages.includes(Pages.Plans)) {
      if (hasAutoAdvancedRef.current) return;
      hasAutoAdvancedRef.current = true;
      dispatch(completePlanSelect(plans[0], { shouldReplaceHistory: true, history }));
    } else {
      dispatch(selectPlan({ planName: selectedPlanName, planSelectComplete: false }));
    }
  }, [plans]);

  const renderPlanSelect = () => {
    switch (offerFlow) {
      case 'avatax-sandbox':
        return <AvataxSandboxPlanSelect plans={plans} />;
      case 'exemptions':
        return <ExemptionsPlanSelect plans={plans as Array<OfferPlanClientDetails & ExemptionsPlanClientDetails>} />;
      case 'ioss':
        return <IossPlanSelect plans={plans as Array<OfferPlanClientDetails & IossPlanClientDetails>} />;
      default:
        return null;
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: using Skylab's component
    <SContainer role='main'>
      <SRow className='pad-top-sm'>
        <SCol>
          <ProgressBar />
        </SCol>
      </SRow>

      {renderPlanSelect()}
    </SContainer>
  );
};

export default OfferPlanSelect;
